import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { Zap, Globe, Bot, Workflow, Shield, Clock } from "lucide-react";

export default function About() {
  return (
    <div className="min-h-screen bg-gradient-subtle flex flex-col">
      <Header />

      <main className="flex-1 max-w-4xl mx-auto px-4 py-12 sm:py-16">
        <article className="space-y-12">
          {/* Hero Section */}
          <header className="text-center space-y-4">
            <div className="inline-flex p-3 bg-gradient-primary rounded-2xl mb-4">
              <Zap className="h-10 w-10 text-white" />
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold bg-gradient-primary bg-clip-text text-transparent">
              About TranslateLoo AI
            </h1>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Breaking language barriers with the power of artificial intelligence
            </p>
          </header>

          {/* Mission Statement */}
          <section className="prose prose-lg dark:prose-invert max-w-none">
            <h2 className="text-2xl font-semibold text-foreground">Our Mission</h2>
            <p className="text-muted-foreground leading-relaxed">
              TranslateLoo AI is built to make translation accessible, fast, and accurate for everyone. We believe that
              language should never be a barrier to communication, understanding, or opportunity. Our platform leverages
              cutting-edge AI technology to deliver high-quality translations in real-time.
            </p>
          </section>

          {/* Features Grid */}
          <section className="space-y-6">
            <h2 className="text-2xl font-semibold text-foreground">What Makes Us Different</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <FeatureCard
                icon={<Bot className="h-6 w-6" />}
                title="AI-Powered Accuracy"
                description="Advanced language models understand context and nuance for more natural translations."
              />
              <FeatureCard
                icon={<Workflow className="h-6 w-6" />}
                title="n8n Integration"
                description="Seamlessly integrated with n8n automation workflows for enterprise-grade reliability."
              />
              <FeatureCard
                icon={<Globe className="h-6 w-6" />}
                title="Multiple Languages"
                description="Support for a wide range of languages with automatic language detection."
              />
              <FeatureCard
                icon={<Clock className="h-6 w-6" />}
                title="Translation History"
                description="Save, search, and organize your translations with persistent local storage."
              />
              <FeatureCard
                icon={<Shield className="h-6 w-6" />}
                title="Privacy Focused"
                description="Your translations are processed securely with no long-term data retention."
              />
              <FeatureCard
                icon={<Zap className="h-6 w-6" />}
                title="Lightning Fast"
                description="Real-time translation with smart caching for instant results."
              />
            </div>
          </section>

          {/* Technology */}
          <section className="bg-translator-panel/50 rounded-2xl p-8 border border-translator-border">
            <h2 className="text-2xl font-semibold text-foreground mb-4">Built with Modern Technology</h2>
            <p className="text-muted-foreground leading-relaxed">
              TranslateLoo AI is built using React, TypeScript, and Tailwind CSS for a fast and responsive user
              experience. Our backend is powered by Supabase Edge Functions, providing a secure and scalable
              infrastructure. The translation engine is connected to n8n automation workflows, allowing for flexible and
              powerful processing pipelines.
            </p>
          </section>

          {/* CTA */}
          <section className="text-center space-y-4 pt-8">
            <h2 className="text-2xl font-semibold text-foreground">Ready to Translate?</h2>
            <p className="text-muted-foreground">
              Start translating text instantly with our free AI-powered translator.
            </p>
            <a
              href="/"
              className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-primary text-white rounded-lg font-medium hover:opacity-90 transition-opacity"
            >
              <Zap className="h-5 w-5" />
              Start Translating
            </a>
          </section>
        </article>
      </main>

      <Footer />
    </div>
  );
}

function FeatureCard({ icon, title, description }: { icon: React.ReactNode; title: string; description: string }) {
  return (
    <div className="p-6 rounded-xl bg-translator-panel/50 border border-translator-border hover:border-primary/30 transition-colors">
      <div className="inline-flex p-2 bg-primary/10 text-primary rounded-lg mb-3">{icon}</div>
      <h3 className="font-semibold text-foreground mb-2">{title}</h3>
      <p className="text-sm text-muted-foreground">{description}</p>
    </div>
  );
}
